import React, { Component } from 'react';
import withStyles from '@mui/styles/withStyles';
import { IconButton, InputAdornment } from '@mui/material';
import Grid from '../../component/form/Grid';
import TextField from '../../component/form/TextField';
import { ImageList, ImageListItem } from '../../component/form/ImageList';
import UploadField from '../../component/form/FileUpload';
import Label from '../../component/form/Label';
import Select from '../../component/form/Select';
import gql from 'graphql-tag';
import { getClient } from '../../apollo';
import { DeleteIcon } from '../../component/IconIndex';
import { GST } from '../../util/products';
import SelectOffice from '../../component/form/SelectOffice';
import PrimaryButton, { AltButton } from '../../component/form/PrimaryButton';
import ColumnLayout from '../../component/ColumnLayout';
import Checkbox from '../../component/form/Checkbox';
import TagButton from '../../component/form/TagButton';
import BiggerMessageBar from '../../component/BiggerMessageBar';
import Separator from '../../component/form/Separator';
import ValidationPlaceholder from '../../component/form/ValidationPlaceholder';

class StockItemVariations extends Component {
    state = {
        allVariationTypes: []
    };

    componentDidMount() {
        this._isMounted = true;
        const loadCategories = async () => {
            const query = gql`
                query ReadAttributeTypes {
                    readAttributeTypes {
                        nodes {
                            ID
                            Name
                            Label
                            Values {
                                nodes {
                                    ID
                                    Value
                                }
                            }
                        }
                    }
                }
            `;
            const { data } = (await getClient().query({ query })) || {};
            return data;
        };
        loadCategories().then(data => {
            if (data && data.readAttributeTypes) {
                if (this._isMounted) {
                    this.setState({
                        allVariationTypes: data.readAttributeTypes.nodes.map(({ __typename, ...parts }) => parts)
                    });
                }
            }
        });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    render() {
        const { form } = this.props;
        const { allVariationTypes } = this.state;
        const variations = form.getField('Variations') || [];
        const variationTypes = form.getField('VariationAttributeTypes') || [];
        const usedIDs = variationTypes.map(obj => obj.ID);
        const options = allVariationTypes
            .filter(opt => !usedIDs.includes(opt.ID))
            .map(opt => ({ label: opt.Label, value: opt.ID }));
        const onClickRemove = ID => {
            if (ID && ID > 0) {
                const VariationAttributeTypes = variationTypes.filter(obj => ID !== obj.ID);
                form.setField({ VariationAttributeTypes });
            }
        };
        const handleNewType = (evt, ID) => {
            if (ID && ID > 0) {
                const VariationAttributeTypes = variationTypes.filter(e => ({ ...e }));
                VariationAttributeTypes.push({ ID });
                form.setField({ VariationAttributeTypes });
            }
        };

        const handleAddVariation = () => {
            const productVariationTypes = form.getField('VariationAttributeTypes') || [];
            variations.push({
                ProductID: form.getField('ID'),
                AttributeValues: productVariationTypes.map(obj => ({ ID: null, TypeID: obj.ID }))
            });
            form.setField({ Variations: variations });
        };

        return (
            <>
                <Grid container spacing={3}>
                    {!variations.length && (
                        <Grid item xs={12}>
                            <BiggerMessageBar messageType="info">
                                <p>There are currently no variations for this product.</p>
                                <p>
                                    If this is supposed to be a type of product with differing attributes and prices
                                    (e.g. a coffin with sizes), select a variation attribute and then add variations.
                                </p>
                            </BiggerMessageBar>
                        </Grid>
                    )}
                    <div style={{ width: '100%', marginRight: 'auto', maxWidth: 550 }}>
                        <Grid bucket>
                            <Grid item xs={12}>
                                <Label>Variation Attributes</Label>
                                <Select
                                    placeholder={
                                        !!variationTypes.length
                                            ? variationTypes.length + ' attribute selected...'
                                            : 'Choose an attribute to create variations...'
                                    }
                                    options={options}
                                    onChange={handleNewType}
                                />
                                {!!variationTypes.length && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {variationTypes.map(obj => (
                                            <TagButton
                                                key={obj.ID}
                                                text={this.getAttributeLabel(obj.ID)}
                                                onClickRemove={() => onClickRemove(obj.ID)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <Separator margin={24} />
                </Grid>

                <div style={{ margin: '6px -12px' }}>
                    {variations.map((obj, idx) => this.renderVariation(obj, idx))}
                </div>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <PrimaryButton onClick={handleAddVariation} disabled={!variationTypes.length}>
                            + Add Variation
                        </PrimaryButton>
                    </Grid>
                </Grid>
            </>
        );
    }

    renderVariation(obj, idx) {
        const { classes, form } = this.props;
        const { allVariationTypes } = this.state;
        const productVariationTypes = form.getField('VariationAttributeTypes') || [];
        const fieldName = `Variations[${idx}]`;
        const isActive = !form.getField(fieldName + '.IsRetired');
        const canArchive = !!form.getField(fieldName + '.canArchive');

        const selectType = options => {
            const selectField = `Variations[${idx}].AttributeValues`;
            const handleSelectType = (val, TypeID, pos) => {
                const AttributeValues = form.getField(selectField) || [];
                const newVal = { ID: val || null, TypeID };
                if (pos in AttributeValues) AttributeValues[pos] = newVal;
                else AttributeValues.push(newVal);
                form.setField({ [selectField]: AttributeValues });
            };
            return options.map((opt, idxT) => {
                const thisField = selectField + `[${idxT}]`;
                const value = form.getField(thisField + '.ID');
                const typeID = form.getField(thisField + '.TypeID');
                const valOption = allVariationTypes.find(vt => vt.ID === opt.ID);
                const options =
                    valOption &&
                    valOption.Values.nodes.map(opt => ({
                        value: opt.ID,
                        label: opt.Value
                    }));
                return (
                    <ValidationPlaceholder key={thisField} form={form} name={thisField}>
                        <Select
                            required
                            value={value}
                            label={this.getAttributeLabel(opt.ID)}
                            options={options || []}
                            onChange={(evt, val) => handleSelectType(val, typeID, idxT)}
                        />
                    </ValidationPlaceholder>
                );
            });
        };

        const handleDelItem = () => {
            const Variations = form.getField('Variations');
            Variations.splice(idx, 1);
            form.setField({ Variations });
        };
        const handleRetired = (evt, val) => {
            form.setField({ [`${fieldName}.IsRetired`]: !val });
        };
        const setBasePrice = (evt, val) => {
            const applyGST = !!form.getField('GST');
            form.setField({ [`${fieldName}.Price`]: applyGST ? val / (GST + 1) : val });
        };

        const setBaseWorth = (evt, val) => {
            const applyGST = !!form.getField('ITC');
            form.setField({ [`${fieldName}.Worth`]: applyGST ? val / (GST + 1) : val });
        };
        const applyITC = form.getField('ITC');
        const applyGST = form.getField('GST');
        return (
            <Grid bucket key={obj.ID || 'k' + idx}>
                <Grid item xs={7}>
                    <Label>Variation #{idx + 1}</Label>
                </Grid>
                <Grid item xs={5}>
                    {obj.ID > 0 && !canArchive ? (
                        <div style={{ textAlign: 'right', marginBottom: -24 }}>
                            <Checkbox
                                checked={isActive}
                                onChange={handleRetired}
                                label="Active?"
                                title="Prevent future use of this variation?"
                            />
                        </div>
                    ) : (
                        <IconButton
                            onClick={handleDelItem}
                            style={{ float: 'right', marginBottom: -24 }}
                            title="Delete this unused variation?"
                        >
                            <DeleteIcon className={classes.deleteIcon} />
                        </IconButton>
                    )}
                </Grid>
                <ColumnLayout>
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    form={form}
                                    name={fieldName + '.InternalItemID'}
                                    label="Item Code/SKU"
                                    placeholder="Leave empty to generate..."
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {selectType(productVariationTypes)}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    form={form}
                                    name={fieldName + '.PurchasePrice'}
                                    type="number"
                                    label={'Purchase Cost' + (applyITC ? ' (inc.GST)' : ' (GST-Free)')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                    onChange={setBaseWorth}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {this.renderItemPhotos(idx)}
                            </Grid>
                        </Grid>
                    </>
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Label>Sales</Label>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    form={form}
                                    name={fieldName + '.SellingPrice'}
                                    type="number"
                                    label={'Retail Price' + (applyGST ? ' (inc.GST)' : ' (GST-Free)')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                    onChange={setBasePrice}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {this.renderOverrides(idx)}
                            </Grid>
                        </Grid>
                    </>
                </ColumnLayout>
                <div style={{ padding: 9 }} />
            </Grid>
        );
    }

    renderItemPhotos(idx) {
        const { form } = this.props;
        const ImageID_Field = `Variations[${idx}].ImageID`;
        const ImageURL_Field = `Variations[${idx}].ImageURL`;
        const ImageID = form.getField(ImageID_Field);
        const ImageURL = form.getField(ImageURL_Field);

        const handleUploadAttachment = uploadFile => {
            const { form } = this.props;
            form.setField({
                [ImageID_Field]: uploadFile.ID,
                [ImageURL_Field]: uploadFile.AbsoluteLink
            });
        };

        const onAttachmentRemove = () => {
            const { form } = this.props;
            form.setField({ [ImageID_Field]: null, [ImageURL_Field]: null });
        };

        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Label>Variation Photo</Label>
                </Grid>
                <Grid item xs={12}>
                    {ImageURL && ImageID > 0 && (
                        <div style={{ paddingBottom: '1rem' }}>
                            <ImageList>
                                <ImageListItem
                                    value={{ tileWidth: 2, image: ImageURL }}
                                    onDelete={onAttachmentRemove}
                                    onClick={() => window.open(ImageURL)}
                                />
                            </ImageList>
                        </div>
                    )}
                    <UploadField
                        folderPath="/productimages"
                        accept=".png, .jpg, .jpeg, .gif"
                        prompt="Upload Photo"
                        publish
                        onComplete={({ uploadFile }) => handleUploadAttachment(uploadFile)}
                    />
                </Grid>
            </Grid>
        );
    }

    renderOverrides(idx) {
        const { form, classes } = this.props;
        const applyGST = !!form.getField('GST');
        const fieldName = `Variations[${idx}].PriceOverrides`;
        const overrides = form.getField(fieldName) || [];
        const disabledOfficeIDs = overrides.map(obj => obj.ID);

        const setBasePrice = (val, idx) => {
            const override = overrides[idx];
            if (applyGST) {
                override.BasePrice = val / (GST + 1);
            } else {
                override.BasePrice = val;
            }
            form.setField({ fieldName: overrides });
        };

        const handleAddPrice = () => {
            overrides.push({});
            form.setField({ fieldName: overrides });
        };

        const handleDelPrice = idx => {
            overrides.splice(idx, 1);
            form.setField({ fieldName: overrides });
        };

        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Label>Price Overrides</Label>
                    {!overrides.length && <p>There are currently no price overrides.</p>}
                </Grid>
                <Grid item xs={12}>
                    {overrides.map((node, idx) => {
                        const nameField = fieldName + `[${idx}]`;
                        return (
                            <Grid container className={classes.listItem} key={node.ID || 'k' + idx}>
                                <Grid item xs={12} sm={7}>
                                    <SelectOffice
                                        fullWidth
                                        allowNone={false}
                                        form={form}
                                        name={`${nameField}.ID`}
                                        label={'Brand/Branch'}
                                        disabledOfficeIDs={disabledOfficeIDs}
                                        allowParent
                                    />
                                </Grid>
                                <Grid item xs={10} sm={4}>
                                    <TextField
                                        form={form}
                                        name={`${nameField}.SellingPrice`}
                                        type="number"
                                        label={'Retail Price' + (applyGST ? ' (inc.GST)' : ' (GST-Free)')}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                                        }}
                                        onChange={(evt, val) => setBasePrice(val, idx)}
                                    />
                                </Grid>
                                <Grid item xs={2} sm={1}>
                                    <IconButton className={classes.iconButton} onClick={() => handleDelPrice(idx)}>
                                        <DeleteIcon className={classes.deleteIcon} />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        );
                    })}
                </Grid>
                <Grid item xs={12}>
                    <AltButton onClick={handleAddPrice}>+ Add Brand Price</AltButton>
                </Grid>
            </Grid>
        );
    }

    getAttributeLabel = ID => {
        const { allVariationTypes } = this.state;
        const val = allVariationTypes.find(obj => ID === obj.ID);
        return val && val.Label;
    };
}

const styles = ({ palette }) => ({
    listItem: {
        width: '100%',
        padding: '0 0 8px 0',
        margin: '3px 0 0 0 !important',
        borderRadius: 5,
        position: 'relative',
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.05)'
        },
        '& > div': {
            padding: 8
        }
    },
    iconButton: {
        marginLeft: -10,
        marginTop: 16,
        width: 32,
        height: 32,
        '& svg': { width: 18, height: 18 }
    },
    deleteIcon: {
        color: palette.button.destroy
    }
});

export default withStyles(styles)(StockItemVariations);
